import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Link, useLocation } from "wouter";
import { LogOut, User, Building2, Shield, ChevronDown } from "lucide-react";

export interface UserMenuProps {
  afterSignOutUrl?: string;
  appearance?: any;
  userProfileProps?: any;
  [key: string]: any;
}

export function UserMenu({ afterSignOutUrl }: UserMenuProps) {
  const { user, signOut } = useAuth();
  const [, setLocation] = useLocation();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!user) return null;

  const initials = `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase() || user.email[0].toUpperCase();
  const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email;

  const handleSignOut = async () => {
    setOpen(false);
    await signOut();
    if (afterSignOutUrl) {
      setLocation(afterSignOutUrl);
    } else {
      setLocation("/sign-in");
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 p-1 rounded-full hover:ring-2 hover:ring-border transition-all focus:outline-none"
        aria-label="User menu"
      >
        {user.avatarUrl ? (
          <img src={user.avatarUrl} alt={fullName} className="w-8 h-8 rounded-full object-cover border border-border" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-500 text-white flex items-center justify-center font-bold text-xs shadow-sm border border-border">
            {initials}
          </div>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-64 bg-card text-foreground border border-border rounded-xl shadow-2xl z-50 py-2 animate-in fade-in zoom-in-95 duration-100">
          {/* User info banner */}
          <div className="px-4 py-3 border-b border-border">
            <p className="text-sm font-semibold text-foreground truncate">{fullName}</p>
            <p className="text-xs text-muted-foreground truncate mt-0.5">{user.email}</p>
            {user.tenantName && (
              <div className="flex items-center gap-1.5 text-[11px] text-blue-700 dark:text-blue-300 mt-2 font-medium bg-blue-500/10 px-2 py-1 rounded">
                <Building2 className="w-3 h-3" />
                <span className="truncate">{user.tenantName}</span>
              </div>
            )}
          </div>

          {/* Links */}
          <div className="py-1">
            <Link
              href="/account-settings"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-4 py-2 text-xs text-foreground/80 hover:text-foreground hover:bg-muted transition-colors"
            >
              <User className="w-3.5 h-3.5" />
              <span>Account Settings</span>
            </Link>

            {user.isSuperAdmin && (
              <Link
                href="/admin/platform"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 px-4 py-2 text-xs text-amber-700 dark:text-amber-300 hover:bg-muted transition-colors font-medium"
              >
                <Shield className="w-3.5 h-3.5" />
                <span>Platform Admin</span>
              </Link>
            )}
          </div>

          {/* Logout */}
          <div className="pt-1 border-t border-border">
            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-2.5 px-4 py-2 text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-500/10 transition-colors text-left"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export const UserButton = UserMenu;
